import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useDashboardStore from '../store/dashboardStore';
import { ssoService } from '../services/sso';
import { toast } from 'react-toastify';
import { isNativeApp } from '../config';

const Sidebar = () => {
  const { currentUser, getPlanConfig, checkUsageLimits } = useDashboardStore((state) => ({
    currentUser: state.currentUser,
    getPlanConfig: state.getPlanConfig,
    checkUsageLimits: state.checkUsageLimits,
  }));
  const limits = getPlanConfig(currentUser.plan);
  const usageLimits = checkUsageLimits();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [recentApps, setRecentApps] = useState([]);
  const [launchingApp, setLaunchingApp] = useState(null);

  // Load recent apps on mount
  useEffect(() => {
    const apps = ssoService.getRecentApps().slice(0, 3); // Show top 3 recent
    setRecentApps(apps);
  }, []);

  // Close sidebar on window resize if going above mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  // Close sidebar on Escape key
  useEffect(() => {
    if (!isSidebarOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSidebarOpen]);

  const handleQuickLaunch = async (appId) => {
    setLaunchingApp(appId);
    try {
      await ssoService.launchApp(appId, true);
      toast.success('App launched!');
      setRecentApps(ssoService.getRecentApps().slice(0, 3));
    } catch (error) {
      toast.error('Failed to launch app');
    } finally {
      setLaunchingApp(null);
    }
  };

  const menuItems = [
    // Core
    { icon: '\uD83D\uDCCA', text: 'Dashboard', path: '/dashboard' },
    { icon: '\uD83D\uDCE1', text: 'Activity Feed', path: '/activity' },

    // Ecosystem Management
    { type: 'divider', label: 'Ecosystem' },
    { icon: '\uD83D\uDC9A', text: 'System Health', path: '/system-health' },
    { icon: '\uD83D\uDC65', text: 'User Management', path: '/users' },
    { icon: '\uD83D\uDE80', text: 'App Marketplace', path: '/apps' },
    { icon: '\uD83D\uDD04', text: 'Onboarding', path: '/onboarding' },

    // Marketplace & Intelligence
    { type: 'divider', label: 'Intelligence' },
    { icon: '\uD83D\uDECD\uFE0F', text: 'Marketplace', path: '/marketplace' },
    { icon: '\uD83E\uDDFE', text: 'Scan', path: '/scan' },
    { icon: '\uD83E\uDD16', text: 'AI Agents', path: '/ai-agents' },
    { icon: '\uD83C\uDFAC', text: 'Video Generator', path: '/video-generator' },
    { icon: '\uD83D\uDCC8', text: 'Analytics', path: '/analytics' },
    { icon: '\uD83D\uDD04', text: 'Scrapers', path: '/scrapers' },
    { icon: '\uD83E\uDD1D', text: 'Seller Accounts', path: '/seller-accounts' },
    { icon: '\uD83D\uDD0D', text: 'Monitoring', path: '/monitoring' },

    // Account
    { type: 'divider', label: 'Account' },
    // Billing/subscription management is web-only \u2014 Apple builds ship free (3.1.1)
    ...(isNativeApp ? [] : [{ icon: '\uD83D\uDCB3', text: 'Billing', path: '/billing' }]),
    { icon: '\uD83D\uDCE4', text: 'Data Export', path: '/data-export' },
    { icon: '\uD83C\uDFA7', text: 'Support', path: '/support' },
    { icon: '\uD83D\uDCDA', text: 'Help & Guides', path: '/help' },
    { icon: '\u2699\uFE0F', text: 'Settings', path: '/settings' },
  ];

  const handleMenuClick = useCallback((item) => {
    navigate(item.path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [navigate]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  return (
    <>
      {/* Hamburger menu for mobile -- visibility controlled by CSS */}
      <button
        type="button"
        className="hamburger-menu"
        onClick={toggleSidebar}
        aria-label="Toggle navigation menu"
        aria-expanded={isSidebarOpen}
      >
        <span aria-hidden="true">{isSidebarOpen ? '\u2715' : '\u2630'}</span>
      </button>

      {/* Overlay for mobile when sidebar is open */}
      {isSidebarOpen && (
        <div
          className="sidebar-overlay sidebar-overlay--visible"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <div
        className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
        role="complementary"
        aria-label="Main navigation sidebar"
      >
        {/* Close button for mobile */}
        <button
          type="button"
          className="sidebar-close"
          onClick={closeSidebar}
          aria-label="Close navigation menu"
        >
          <span aria-hidden="true">{'\u2715'}</span>
        </button>

        <button
          type="button"
          className="logo"
          onClick={() => {
            navigate('/dashboard');
            if (window.innerWidth < 768) closeSidebar();
          }}
          aria-label="Go to Dashboard"
        >
          <div className="logo-icon" aria-hidden="true">{'\u26A1'}</div>
          <div className="logo-text">QuantrolixAI</div>
        </button>

        <nav className="nav-menu sidebar-nav" aria-label="Main navigation">
          {menuItems.map((item, index) => {
            // Render section dividers
            if (item.type === 'divider') {
              return (
                <div
                  key={`divider-${index}`}
                  style={{
                    padding: '12px 16px 6px',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: 'rgba(255, 255, 255, 0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '1.2px',
                    marginTop: index > 0 ? '4px' : 0,
                    borderTop: index > 2 ? '1px solid rgba(102, 126, 234, 0.1)' : 'none',
                    paddingTop: index > 2 ? '12px' : '12px',
                  }}
                >
                  {item.label}
                </div>
              );
            }

            return (
              <button
                type="button"
                key={index}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleMenuClick(item)}
                aria-label={`Navigate to ${item.text}`}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                title={item.text}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                <span className="nav-text">{item.text}</span>
              </button>
            );
          })}
        </nav>

        {/* Quick Launch Apps */}
        {recentApps.length > 0 && (
          <div className="quick-launch" style={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(102, 126, 234, 0.2)',
            marginBottom: '0',
          }}>
            <div style={{
              fontSize: '0.7rem',
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '10px',
            }} id="quick-launch-label">
              Quick Launch
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
            }} role="group" aria-labelledby="quick-launch-label">
              {recentApps.map((appId) => {
                const appConfig = ssoService.getAppConfig(appId);
                if (!appConfig) return null;
                return (
                  <button
                    key={appId}
                    onClick={() => handleQuickLaunch(appId)}
                    disabled={launchingApp === appId}
                    title={appConfig.name}
                    aria-label={`Launch ${appConfig.name}`}
                    style={{
                      width: '36px',
                      height: '36px',
                      minWidth: '36px',
                      minHeight: '36px',
                      background: launchingApp === appId
                        ? 'rgba(102, 126, 234, 0.3)'
                        : 'rgba(102, 126, 234, 0.15)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      cursor: launchingApp === appId ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (launchingApp !== appId) {
                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.3)';
                        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (launchingApp !== appId) {
                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                      }
                    }}
                  >
                    {launchingApp === appId ? (
                      <span className="loading-spinner" style={{ width: '14px', height: '14px' }} aria-hidden="true"></span>
                    ) : (
                      <span aria-hidden="true">{appConfig.icon}</span>
                    )}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  navigate('/apps');
                  if (window.innerWidth < 768) closeSidebar();
                }}
                title="Browse all apps"
                aria-label="Browse all apps"
                style={{
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  minHeight: '36px',
                  background: 'transparent',
                  border: '1px dashed rgba(102, 126, 234, 0.3)',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.5)',
                }}
              >
                <span aria-hidden="true">+</span>
              </button>
            </div>
          </div>
        )}

        <div className="usage-display" role="region" aria-label="Usage statistics">
          <div className="usage-item">
            <div className="usage-label">
              <span>Product Scans</span>
              <span>{currentUser.usage.scans.toLocaleString()} / {limits.scans.toLocaleString()}</span>
            </div>
            <div className="usage-bar" role="progressbar" aria-valuenow={usageLimits.scans.percentage} aria-valuemin={0} aria-valuemax={100} aria-label={`Product scans: ${usageLimits.scans.percentage}% used`}>
              <div
                className="usage-bar-fill"
                style={{
                  width: `${usageLimits.scans.percentage}%`,
                  background: usageLimits.scans.isNearLimit
                    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                    : 'linear-gradient(135deg, #667eea, #00d4ff)'
                }}
              />
            </div>
          </div>

          <div className="usage-item">
            <div className="usage-label">
              <span>API Calls</span>
              <span>{currentUser.usage.api_calls.toLocaleString()} / {limits.api_calls.toLocaleString()}</span>
            </div>
            <div className="usage-bar" role="progressbar" aria-valuenow={usageLimits.api_calls.percentage} aria-valuemin={0} aria-valuemax={100} aria-label={`API calls: ${usageLimits.api_calls.percentage}% used`}>
              <div
                className="usage-bar-fill"
                style={{
                  width: `${usageLimits.api_calls.percentage}%`,
                  background: usageLimits.api_calls.isNearLimit
                    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                    : 'linear-gradient(135deg, #667eea, #00d4ff)'
                }}
              />
            </div>
          </div>
        </div>

        <div className="plan-badge" aria-label={`Current plan: ${limits?.name || 'Plan'}`}>
          {limits?.name?.toUpperCase?.() || 'PLAN' }
        </div>
      </div>
    </>
  );
};

export default Sidebar;
